import { Log } from "@fanfanlo";
import { IProject } from "../entities/IProject";
import { ITriggerActionGroup } from "../entities/ITriggerActionGroup";
import { ITriggerAction } from "../entities/ITriggerAction";
import { IActionGroup } from "../entities/IActionGroup";
import { ITriggerGroup } from "../entities/ITriggerGroup";
import { IAction } from "../entities/IAction";
import { ITrigger } from "../entities/ITrigger";

const fileLog = new Log(false, 'description_entityFinder');
fileLog.pause = true;
fileLog.childrenPaused = true;

/**
 * 实体查找参数接口
 * 注意：v2 使用 clientId 和 ulid，而不是 v1 的 client_id 和 id
 */
export interface IEntityFinderParams {
    projectClientId?: string
    triggerActionGroupUlid?: string
    triggerActionUlid?: string
    actionGroupUlid?: string
    actionUlid?: string
    triggerGroupUlid?: string
    triggerUlid?: string
}

/**
 * 实体类型
 */
export type EntityType =
    | 'project'
    | 'triggerActionGroup'
    | 'triggerAction'
    | 'actionGroup'
    | 'action'
    | 'triggerGroup'
    | 'trigger';

/**
 * 实体查找结果
 */
export interface IEntityFinderResult {
    entity: any
    type: EntityType
}

/**
 * 在TriggerActionGroup中查找TriggerAction
 */
function findInTriggerActionGroup(
    group: ITriggerActionGroup,
    ulid: string
): ITriggerAction | null {
    const fnLog = fileLog.sub(false, 'findInTriggerActionGroup_fn');
    fnLog.pause = true;

    fnLog.log('开始查找TriggerAction, group:', group, 'ulid:', ulid);

    if (!group) {
        fnLog.log('group为null');
        return null;
    }

    if (!group.children) {
        fnLog.log('group.children为null');
        return null;
    }

    if (!Array.isArray(group.children)) {
        fnLog.log('group.children不是数组');
        return null;
    }

    const result = group.children.find(ta => ta.ulid === ulid) || null;
    fnLog.log('查找结果:', result);

    return result;
}

/**
 * 在ActionGroup中查找Action
 */
function findInActionGroup(
    group: IActionGroup,
    ulid: string
): IAction | null {
    const fnLog = fileLog.sub(false, 'findInActionGroup_fn');
    fnLog.pause = true;

    fnLog.log('开始查找Action, group:', group, 'ulid:', ulid);

    if (!group) {
        fnLog.log('group为null');
        return null;
    }

    if (!group.list) {
        fnLog.log('group.list为null');
        return null;
    }

    if (!Array.isArray(group.list)) {
        fnLog.log('group.list不是数组');
        return null;
    }

    const result = group.list.find(a => a.ulid === ulid) || null;
    fnLog.log('查找结果:', result);

    return result;
}

/**
 * 在TriggerGroup中查找Trigger
 */
function findInTriggerGroup(
    group: ITriggerGroup,
    ulid: string
): ITrigger | null {
    const fnLog = fileLog.sub(false, 'findInTriggerGroup_fn');
    fnLog.pause = true;

    fnLog.log('开始查找Trigger, group:', group, 'ulid:', ulid);

    if (!group) {
        fnLog.log('group为null');
        return null;
    }

    if (!group.list) {
        fnLog.log('group.list为null');
        return null;
    }

    if (!Array.isArray(group.list)) {
        fnLog.log('group.list不是数组');
        return null;
    }

    const result = group.list.find(t => t.ulid === ulid) || null;
    fnLog.log('查找结果:', result);

    return result;
}

/**
 * 在单个project中查找实体
 */
function findEntityInProject(
    project: IProject,
    params: IEntityFinderParams
): IEntityFinderResult | null {
    const fnLog = fileLog.sub(false, 'findEntityInProject_fn');
    fnLog.pause = true;

    fnLog.log('开始在project中查找实体, project:', project, 'params:', params);

    const {
        triggerActionGroupUlid,
        triggerActionUlid,
        actionGroupUlid,
        actionUlid,
        triggerGroupUlid,
        triggerUlid
    } = params;

    if (!project) {
        fnLog.log('project为null');
        return null;
    }

    if (triggerActionGroupUlid) {
        fnLog.log('查找triggerActionGroup, ulid:', triggerActionGroupUlid);

        if (!project.children || project.children.length === 0) {
            fnLog.log('project.children为空');
            return null;
        }

        const triggerActionGroup = project.children.find(
            child => child.ulid === triggerActionGroupUlid
        );

        if (triggerActionGroup) {
            fnLog.log('找到triggerActionGroup');
            return {
                entity: triggerActionGroup,
                type: 'triggerActionGroup'
            };
        } else {
            fnLog.log('未找到triggerActionGroup');
        }
    }

    if (triggerActionUlid) {
        fnLog.log('查找triggerAction, ulid:', triggerActionUlid);

        if (!project.children || project.children.length === 0) {
            fnLog.log('project.children为空');
            return null;
        }

        for (const triggerActionGroup of project.children) {
            const triggerAction = findInTriggerActionGroup(
                triggerActionGroup,
                triggerActionUlid
            );

            if (triggerAction) {
                fnLog.log('找到triggerAction');

                if (actionGroupUlid || actionUlid) {
                    const actionGroup = triggerAction.actionGroup;
                    fnLog.log('查找actionGroup或action');

                    if (actionGroupUlid && actionGroup && actionGroup.ulid === actionGroupUlid) {
                        fnLog.log('找到actionGroup');
                        return {
                            entity: actionGroup,
                            type: 'actionGroup'
                        };
                    }

                    if (actionUlid && actionGroup) {
                        const action = findInActionGroup(actionGroup, actionUlid);
                        if (action) {
                            fnLog.log('找到action');
                            return {
                                entity: action,
                                type: 'action'
                            };
                        } else {
                            fnLog.log('未找到action');
                        }
                    }
                }

                if (triggerGroupUlid || triggerUlid) {
                    const triggerGroup = triggerAction.triggerGroup;
                    fnLog.log('查找triggerGroup或trigger');

                    if (triggerGroupUlid && triggerGroup && triggerGroup.ulid === triggerGroupUlid) {
                        fnLog.log('找到triggerGroup');
                        return {
                            entity: triggerGroup,
                            type: 'triggerGroup'
                        };
                    }

                    if (triggerUlid && triggerGroup) {
                        const trigger = findInTriggerGroup(triggerGroup, triggerUlid);
                        if (trigger) {
                            fnLog.log('找到trigger');
                            return {
                                entity: trigger,
                                type: 'trigger'
                            };
                        } else {
                            fnLog.log('未找到trigger');
                        }
                    }
                }

                fnLog.log('返回triggerAction');
                return {
                    entity: triggerAction,
                    type: 'triggerAction'
                };
            }
        }

        fnLog.log('未找到triggerAction');
    }

    fnLog.log('返回project');
    return {
        entity: project,
        type: 'project'
    };
}

/**
 * 在所有projects中查找实体
 */
export function findEntityAcrossProjects(
    allProjects: IProject[],
    params: IEntityFinderParams
): IEntityFinderResult | null {
    const fnLog = fileLog.sub(false, 'findEntityAcrossProjects_fn');
    fnLog.pause = true;

    fnLog.log('开始在所有projects中查找实体, projects数量:', allProjects.length, 'params:', params);

    if (!allProjects) {
        fnLog.log('allProjects为null');
        return null;
    }

    if (!Array.isArray(allProjects)) {
        fnLog.log('allProjects不是数组');
        return null;
    }

    const { projectClientId } = params;

    if (projectClientId) {
        fnLog.log('查找project, clientId:', projectClientId);
        const project = allProjects.find(p => p.clientId === projectClientId);

        if (!project) {
            fnLog.log('未找到project');
            return null;
        }

        fnLog.log('找到project, 开始在project中查找实体');
        const result = findEntityInProject(project, params);
        return result;
    }

    fnLog.log('未提供projectClientId, 遍历所有projects查找');

    for (let i = 0; i < allProjects.length; i++) {
        const project = allProjects[i];
        fnLog.log('检查project, index:', i, 'clientId:', project.clientId);

        const result = findEntityInProject(project, params);
        if (result) {
            fnLog.log('找到实体, type:', result.type);
            return result;
        }
    }

    fnLog.log('未找到任何实体');
    return null;
}
